import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import { useIntl } from "react-intl";
import { useInstagramCore } from "../../../../cores/instagram";
import { useInstagramLoginCore } from "../../../../cores/instagramLogin";
import { useMeCore } from "../../../../cores/me";
import { useGlobalCore } from "../../../../cores/globals";
import { INSTAGRAM_BIND_MODAL } from "../../../../constants/modal.constant";
import { INSTAGRAM_SYNC_LOADING } from "../../../../constants/loading.constant";
import InstagramLoginForm from "../Instagram/InstagramLoginForm";
import InstagramConnectionMethodModal from "./InstagramConnectionMethodModal";
import { daysRemainingFromToday } from "../../../utils/dateUtils";

/**
 * Selector de método de conexión Instagram.
 * Flujo vía Facebook: OAuth → elegir par IG + Page → bind publicaciones + mensajería.
 */
export default function InstagramConnectionSelector() {
  const intl = useIntl();
  const {
    settings: facebookPathSettings,
    unbind: unbindFacebookPath,
    getAccounts,
  } = useInstagramCore();
  const {
    get: getLoginSettings,
    settings: loginPathSettings,
    unbind: unbindLoginPath,
  } = useInstagramLoginCore();
  const { company, getMeSocialSettings } = useMeCore();
  const { turnOnModal, isLoading } = useGlobalCore();
  const [methodModalOpen, setMethodModalOpen] = useState(false);

  useEffect(() => {
    if (company?.id) {
      getLoginSettings(company.id);
      getMeSocialSettings(company.id);
    }
    // Only hydrate on company change — avoid racing get() with unbind.
  }, [company?.id, getLoginSettings, getMeSocialSettings]);

  const isFacebookPathConnected = useMemo(
    () => !_.isEmpty(facebookPathSettings),
    [facebookPathSettings]
  );
  const isLoginPathConnected = useMemo(
    () => !_.isEmpty(loginPathSettings?.token),
    [loginPathSettings]
  );
  const isConnected = isFacebookPathConnected || isLoginPathConnected;

  const activeSettings = isFacebookPathConnected
    ? facebookPathSettings
    : loginPathSettings;

  const remainingBindDays = useMemo(
    () =>
      activeSettings?.expiresIn
        ? daysRemainingFromToday(activeSettings.expiresIn)
        : null,
    [activeSettings]
  );

  const handleFacebookOAuthCompleted = useCallback(
    async (accessToken: string) => {
      setMethodModalOpen(false);
      await getAccounts(company!.id, accessToken);
      turnOnModal(
        INSTAGRAM_BIND_MODAL,
        <InstagramLoginForm accessToken={accessToken} />
      );
    },
    [company, getAccounts, turnOnModal]
  );

  const handleUnbind = useCallback(async () => {
    const companyId = company!.id;
    if (isFacebookPathConnected) {
      await unbindFacebookPath(companyId);
    } else if (isLoginPathConnected) {
      await unbindLoginPath(companyId);
    }
  }, [
    company,
    isFacebookPathConnected,
    isLoginPathConnected,
    unbindFacebookPath,
    unbindLoginPath,
  ]);

  if (isConnected) {
    return (
      <div>
        {isLoginPathConnected && (
          <p className="small text-muted mb-2">
            {intl.formatMessage({ id: "app.instagramLogin.connectedViaInstagram" })}
            {loginPathSettings?.username ? ` @${loginPathSettings.username}` : ""}
          </p>
        )}
        {isFacebookPathConnected && (
          <p className="small text-muted mb-2">
            {intl.formatMessage({ id: "app.instagramLogin.connectedViaFacebook" })}
          </p>
        )}
        {remainingBindDays != null && remainingBindDays <= 7 && (
          <p className="small text-warning mb-2">
            {intl.formatMessage(
              { id: "app.instagramLogin.expiresIn" },
              { days: remainingBindDays }
            )}
          </p>
        )}
        <Button
          className="w-100"
          color="danger"
          outline
          onClick={handleUnbind}
          disabled={isLoading(INSTAGRAM_SYNC_LOADING)}
        >
          {intl.formatMessage({ id: "app.home.disconnect" })}
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button
        className="w-100 mx-auto"
        color="primary"
        onClick={() => setMethodModalOpen(true)}
      >
        {intl.formatMessage({ id: "app.home.syncUp" })}
      </Button>

      <InstagramConnectionMethodModal
        isOpen={methodModalOpen}
        onClose={() => setMethodModalOpen(false)}
        onFacebookOAuthCompleted={handleFacebookOAuthCompleted}
      />
    </>
  );
}
